import { Button } from '@components/button/button'
import { URLS } from '@components/onboarding/urls'
import { Spinner } from '@components/spinner'
import type { Tier } from '@getstep/proto/tier'
import { TierType } from '@getstep/proto/tier'
import { notNullish, nullish } from '@getstep/sdk/dist/util/Assert'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { useOnboarding } from '@lib/hooks/useOnboarding'
import { useSession } from '@lib/store/useStores'
import { observer } from 'mobx-react-lite'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import styles from './exclusive-offer.module.scss'

export const ExclusiveOffer = observer(() => {
    const sessionStore = useSession()
    const router = useRouter()
    const [tier, setTier] = useState<Tier | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)
    const [isScreenLoading, setIsScreenLoading] = useState(true)
    const onboarding = useOnboarding()

    useEffect(() => {
        onboarding.setIsLoading(isLoading)
    }, [isLoading])

    useEffect(() => {
        onboarding.setHeaderText(isScreenLoading ? '' : 'Welcome to Step')
        onboarding.setHeaderBackButtonVisible(false)
        setIsLoading(false)
    }, [isScreenLoading])

    useEffectAsync(async () => {
        setIsScreenLoading(true)
        if (!sessionStore.loaded) {
            await sessionStore.restore()
        }
        if (nullish(tier)) {
            const publicApi = await sessionStore.sdk.createPublicApi()
            const tierResponse = await publicApi.getTier(TierType.BLACK)
            setTier(tierResponse.tier)
        }
        if (notNullish(sessionStore.authorizedStore)) {
            // Use enrollment flow manager from authorized store
            await sessionStore.authorizedStore.enrollmentFlowManager.navigateToNextStep(
                router,
            )
            return
        }
        setIsScreenLoading(false)
    }, [])

    if (isScreenLoading) {
        return <Spinner />
    }

    return (
        <div className={styles.exclusiveOffer}>
            <div className={styles.imageContainer}>
                <Image
                    priority
                    src='/images/onboarding/borrow-cash.webp'
                    alt='Borrow up to $250 with Step EarlyPay!'
                    width={736}
                    height={375}
                    className={styles.image}
                />
            </div>

            <div className={styles.copyContainer}>
                <h2 className={styles.title}>
                    Borrow up to $250 with Step EarlyPay!
                </h2>
                <p>
                    Connect your bank account and start your free 7-day trial of
                    Step Black to find out if you&apos;re eligible.
                </p>

                <div className={styles.iconCopyContainer}>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/onboarding/instant-icon.svg'
                                alt='Borrow up to $250 with Step EarlyPay'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            <strong>Get instant access</strong> to cash¹
                        </span>
                    </div>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/onboarding/credit-icon.webp'
                                alt='Help build your credit'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            <strong>Build credit</strong> just by using your
                            Step Card
                        </span>
                    </div>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/onboarding/lending-icon-3.webp'
                                alt='Earn unlimited cashback on Step Visa card purchases'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            <strong>Get up to 10% cashback</strong> on purchases
                        </span>
                    </div>
                </div>

                <div className={styles.disclaimer}>
                    <p>
                        ¹Loan eligibility and amounts are subject to eligibility
                        requirements. Instant delivery is available for a fee
                        and typically occurs in seconds, but may take up to 30
                        minutes. No fee to receive loans by ACH transfer. See{' '}
                        <a
                            href='/policies/step-borrow-cash-pay-in-one-agreement'
                            target='_blank'
                        >
                            Step EarlyPay Agreement
                        </a>{' '}
                        for details.
                    </p>
                    <p>
                        See additional terms and conditions in{' '}
                        <a
                            href='/policies/step-product-disclosures'
                            target='_blank'
                        >
                            Step Product Disclosures
                        </a>
                    </p>
                </div>

                <div className={styles.footer}>
                    <Button
                        size='medium'
                        preset='futureFuscia'
                        className={styles.button}
                        onClick={() => {
                            setIsLoading(true)
                            router.replace(URLS.PHONE_VERIFICATION)
                        }}
                        loading={isLoading}
                    >
                        Apply Now
                    </Button>
                </div>
            </div>
        </div>
    )
})

export default ExclusiveOffer
